import { useState } from 'react';
import jsPDF from 'jspdf';
import type { BrandingAnalyzeResponse, CheckResult, ScanType } from '../../api/brandingApi';
import ScanResultSummary from './ScanResultSummary';
import CheckCategoryAccordion from './CheckCategoryAccordion';
import EmailReportCapture from './EmailReportCapture';
import PublicCtaBox from './PublicCtaBox';

interface Props {
  result: BrandingAnalyzeResponse;
  scanType: ScanType;
  showEmail?: boolean;
  onScanAgain: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  ROBOTS: 'Robots.txt',
  SITEMAP: 'Sitemap',
  HTTPS: 'HTTPS & Security',
  DNS_INFRA: 'DNS & Infrastructure',
  PERFORMANCE_BASIC: 'Performance',
  PERFORMANCE: 'Performance',
  INDEXABILITY: 'Indexability',
  OWNERSHIP: 'Verification Signals',
  GSC_OWNER: 'GSC Verification Signals',
  TITLE_META: 'Title & Meta',
  HEADINGS: 'Headings',
  PAGE_TECHNICAL: 'Page Technical',
  ON_PAGE_SEO: 'On-Page SEO',
  CANONICAL: 'Canonical',
  STRUCTURED_DATA: 'Structured Data',
  SCHEMA: 'Structured Data',
  SOCIAL: 'Social',
  AI_CRAWLING: 'AI Crawler Access',
  AI_READINESS: 'AI-Readable Structure',
  LLM_DISCOVERY: 'LLM Discovery',
  ENTITY_SIGNALS: 'Entity & Trust',
  AI_CITATION: 'AI Citation',
};

const CATEGORY_CONTEXT: Record<string, string> = {
  ROBOTS: 'Controls how search engines and AI crawlers access the website.',
  SITEMAP: 'Helps search engines discover important URLs efficiently.',
  HTTPS: 'Protects visitors and supports trusted search visibility.',
  DNS_INFRA: 'Confirms the website infrastructure resolves reliably.',
  PERFORMANCE_BASIC: 'Affects loading speed, user experience, and crawl efficiency.',
  PERFORMANCE: 'Affects loading speed, user experience, and crawl efficiency.',
  INDEXABILITY: 'Determines whether important pages can appear in search results.',
  OWNERSHIP: 'Detects observable search platform verification signals.',
  GSC_OWNER: 'Detects Google Search Console verification signals without confirming property access.',
  TITLE_META: 'Controls the main search result title and description signals.',
  HEADINGS: 'Helps users and search engines understand page structure.',
  PAGE_TECHNICAL: 'Checks page-level technical SEO requirements.',
  ON_PAGE_SEO: 'Reviews the core on-page optimization signals.',
  CANONICAL: 'Helps search engines select the preferred page URL.',
  STRUCTURED_DATA: 'Helps search engines and AI systems understand entities and page meaning.',
  SCHEMA: 'Helps search engines and AI systems understand entities and page meaning.',
  SOCIAL: 'Improves preview quality when the page is shared.',
  AI_CRAWLING: 'Checks whether AI crawlers can access and understand the site.',
  AI_READINESS: 'Reviews content structure for AI answer and citation readiness.',
  LLM_DISCOVERY: 'Checks discoverability signals for large language model crawlers.',
  ENTITY_SIGNALS: 'Reviews trust and entity signals used for brand understanding.',
  AI_CITATION: 'Checks whether the page is easy for AI systems to cite accurately.',
};

const CATEGORY_ORDER_BY_SCAN: Partial<Record<ScanType, string[]>> = {
  SITE: [
    'ROBOTS',
    'SITEMAP',
    'HTTPS',
    'DNS_INFRA',
    'PERFORMANCE_BASIC',
    'INDEXABILITY',
    'OWNERSHIP',
    'STRUCTURED_DATA',
    'SOCIAL',
  ],
  PAGE: [
    'PAGE_TECHNICAL',
    'PERFORMANCE_BASIC',
    'TITLE_META',
    'HEADINGS',
    'CANONICAL',
    'INDEXABILITY',
    'ON_PAGE_SEO',
    'STRUCTURED_DATA',
    'SOCIAL',
  ],
  ROBOTS: ['ROBOTS'],
  SITEMAP: ['SITEMAP'],
  GSC_OWNER: ['GSC_OWNER', 'OWNERSHIP'],
  SCHEMA: ['STRUCTURED_DATA', 'SCHEMA'],
  AI_CRAWLING: [
    'AI_CRAWLING',
    'LLM_DISCOVERY',
    'AI_READINESS',
    'ENTITY_SIGNALS',
    'AI_CITATION',
  ],
};

const SEVERITY_WEIGHT: Record<string, number> = {
  HIGH: 4,
  MEDIUM: 3,
  LOW: 2,
  INFO: 1,
};

const ASNERIS_LOGO_PATH = '/asneris.png';

const LEARN_MORE_HREF_BY_SCAN: Record<ScanType, string> = {
  SITE: '/seo-concepts/website-health-check/',
  PAGE: '/seo-concepts/page-seo/',
  ROBOTS: '/seo-concepts/robots-txt-seo/',
  SITEMAP: '/seo-concepts/sitemap-seo/',
  GSC_OWNER: '/seo-concepts/search-engine-owner-seo/',
  SCHEMA: '/seo-concepts/schema-seo/',
  AI_CRAWLING: '/seo-concepts/ai-crawling-geo/',
  GA4: '/seo-concepts/ga4-onpage-seo/',
};

function groupChecks(checks: CheckResult[]): Record<string, CheckResult[]> {
  return checks.reduce<Record<string, CheckResult[]>>((groups, check) => {
    const category = check.category || 'OTHER';
    (groups[category] ||= []).push(check);
    return groups;
  }, {});
}

function averageScore(checks: CheckResult[]): number {
  if (!checks.length) return 0;
  return Math.round(
    checks.reduce((sum, check) => sum + (Number.isFinite(check.score) ? (check.score ?? 0) : 0), 0) /
      checks.length,
  );
}

function scoreLabel(score: number): string {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 55) return 'Needs work';
  return 'Poor';
}

function scoreColor(score: number): string {
  if (score >= 75) return '#15803d';
  if (score >= 55) return '#d97706';
  return '#dc2626';
}

function categoryMetrics(checks: CheckResult[]) {
  return {
    passed: checks.filter((check) => check.status === 'PASS').length,
    warnings: checks.filter((check) => check.status === 'WARNING').length,
    failed: checks.filter((check) => check.status === 'FAIL').length,
    score: averageScore(checks),
  };
}

function estimatedPotentialScore(result: BrandingAnalyzeResponse): number {
  const checks = result.checks ?? [];
  if (!checks.length) return result.overallScore;

  const recoverableAverage = checks.reduce((sum, check) => {
    if (check.status === 'PASS') return sum;
    return sum + Math.max(0, 100 - (check.score ?? 0));
  }, 0) / checks.length;

  return Math.min(100, Math.round(result.overallScore + recoverableAverage));
}

function formatCheckName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function checkStatusLabel(status: string): string {
  if (status === 'PASS') return 'Pass';
  if (status === 'WARNING') return 'Warning';
  if (status === 'FAIL') return 'Issue';
  return status || 'Info';
}

function statusPdfColor(status: string): [number, number, number] {
  if (status === 'PASS') return [21, 128, 61];
  if (status === 'WARNING') return [217, 119, 6];
  if (status === 'FAIL') return [220, 38, 38];
  return [37, 99, 235];
}

function scorePdfColor(score: number): [number, number, number] {
  if (score >= 75) return [21, 128, 61];
  if (score >= 55) return [217, 119, 6];
  return [220, 38, 38];
}

function safeFilenamePart(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[^a-z0-9.-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'website';
}

function recipientLabel(result: BrandingAnalyzeResponse): string {
  if (result.businessName && result.customerName) {
    return `${result.businessName} - ${result.customerName}`;
  }
  return result.businessName || result.customerName || result.domain || 'Website owner';
}

async function loadImageDataUrl(src: string): Promise<string | null> {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function drawPdfBranding(
  pdf: jsPDF,
  pageNumber: number,
  totalPages: number,
  result: BrandingAnalyzeResponse,
  logoDataUrl: string | null,
) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, pageWidth, 24, 'F');
  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', 10, 6, 16, 12);
  } else {
    pdf.setFillColor(15, 54, 99);
    pdf.circle(14, 12, 5, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('A', 14, 14.5, { align: 'center' });
  }

  pdf.setTextColor(15, 54, 99);
  pdf.setFontSize(13);
  pdf.text('Asneris', 30, 10.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(82, 102, 132);
  pdf.text('Website Health Check Report', 30, 16);

  pdf.setFontSize(8);
  pdf.setTextColor(15, 54, 99);
  pdf.text(result.domain ?? 'Website report', pageWidth - 10, 10.5, { align: 'right' });
  pdf.setTextColor(82, 102, 132);
  pdf.text(`Generated ${generatedAt}`, pageWidth - 10, 16, { align: 'right' });
  pdf.setDrawColor(218, 228, 240);
  pdf.line(10, 23, pageWidth - 10, 23);

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
  pdf.setDrawColor(218, 228, 240);
  pdf.line(10, pageHeight - 19, pageWidth - 10, pageHeight - 19);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(82, 102, 132);
  pdf.text(
    'Disclaimer: This report is informational only and does not guarantee search rankings, AI visibility, revenue, or platform outcomes.',
    10,
    pageHeight - 12,
  );
  pdf.setFontSize(8);
  pdf.text('Asneris - Accurate. Actionable. Free.', 10, pageHeight - 6);
  pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 10, pageHeight - 6, { align: 'right' });
}

function ensurePdfSpace(pdf: jsPDF, y: number, neededHeight: number) {
  const pageHeight = pdf.internal.pageSize.getHeight();
  if (y + neededHeight <= pageHeight - 26) return y;
  pdf.addPage();
  return 32;
}

function writeWrappedText(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
) {
  const lines = pdf.splitTextToSize(text, maxWidth) as string[];
  pdf.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function drawPdfTableHeader(pdf: jsPDF, margin: number, y: number, contentWidth: number) {
  const columns = [
    { label: 'Check', x: margin + 3, width: 29 },
    { label: 'Status', x: margin + 34, width: 17 },
    { label: 'Severity', x: margin + 53, width: 20 },
    { label: 'Score', x: margin + 75, width: 16 },
    { label: 'Detail', x: margin + 93, width: 41 },
    { label: 'Recommendation', x: margin + 136, width: contentWidth - 139 },
  ];

  pdf.setFillColor(248, 251, 254);
  pdf.setDrawColor(218, 228, 240);
  pdf.rect(margin, y, contentWidth, 8, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.8);
  pdf.setTextColor(82, 102, 132);
  columns.forEach((column) => pdf.text(column.label.toUpperCase(), column.x, y + 5.2));
  return y + 8;
}

function drawPdfDetailTableRow(
  pdf: jsPDF,
  check: CheckResult,
  margin: number,
  y: number,
  contentWidth: number,
) {
  const recommendation =
    (check.status === 'FAIL' || check.status === 'WARNING') && check.recommendation
      ? check.recommendation
      : '';
  const checkLines = (pdf.splitTextToSize(formatCheckName(check.checkName), 27) as string[]).slice(0, 3);
  const detailLines = pdf.splitTextToSize(check.message ?? '', 39) as string[];
  const recommendationLines = pdf.splitTextToSize(recommendation, contentWidth - 141) as string[];
  const rowHeight = Math.max(13, checkLines.length * 4 + 6, detailLines.length * 4 + 6, recommendationLines.length * 4 + 6);
  const [r, g, b] = statusPdfColor(check.status);

  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(228, 237, 246);
  pdf.rect(margin, y, contentWidth, rowHeight, 'FD');
  pdf.setDrawColor(238, 244, 250);
  [margin + 32, margin + 51, margin + 73, margin + 91, margin + 134].forEach((x) => {
    pdf.line(x, y, x, y + rowHeight);
  });

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.4);
  pdf.setTextColor(15, 54, 99);
  pdf.text(checkLines, margin + 3, y + 5);

  pdf.setFontSize(6.8);
  pdf.setTextColor(r, g, b);
  pdf.text(checkStatusLabel(check.status), margin + 34, y + 5);
  pdf.text(check.severity, margin + 53, y + 5);
  pdf.text(`${check.score ?? 0}/100`, margin + 89, y + 5, { align: 'right' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.2);
  pdf.setTextColor(47, 65, 92);
  pdf.text(detailLines, margin + 93, y + 5);
  pdf.setTextColor(check.status === 'FAIL' ? 153 : check.status === 'WARNING' ? 146 : 82, check.status === 'FAIL' ? 27 : check.status === 'WARNING' ? 64 : 102, check.status === 'FAIL' ? 27 : check.status === 'WARNING' ? 14 : 132);
  pdf.text(recommendationLines, margin + 136, y + 5);

  return y + rowHeight;
}

function drawPdfPassedSummary(
  pdf: jsPDF,
  checks: CheckResult[],
  margin: number,
  y: number,
  contentWidth: number,
) {
  if (!checks.length) return y;
  const passedNames = checks.map((check) => formatCheckName(check.checkName)).join(', ');
  const lines = pdf.splitTextToSize(`Passed checks (${checks.length}): ${passedNames}`, contentWidth - 10) as string[];
  const height = Math.max(10, lines.length * 4 + 6);
  y = ensurePdfSpace(pdf, y, height + 3);
  pdf.setFillColor(240, 253, 244);
  pdf.setDrawColor(187, 247, 208);
  pdf.roundedRect(margin, y, contentWidth, height, 2, 2, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(21, 128, 61);
  pdf.text(lines, margin + 5, y + 6);
  return y + height + 4;
}

function drawPdfActionPlan(
  pdf: jsPDF,
  issues: CheckResult[],
  margin: number,
  y: number,
  contentWidth: number,
) {
  y = ensurePdfSpace(pdf, y, 22);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Recommended Action Plan', margin, y);
  y += 8;

  if (!issues.length) {
    pdf.setFillColor(240, 253, 244);
    pdf.setDrawColor(187, 247, 208);
    pdf.roundedRect(margin, y, contentWidth, 14, 2, 2, 'FD');
    pdf.setFontSize(8);
    pdf.setTextColor(22, 101, 52);
    pdf.text('No priority fixes are required. Continue monitoring after content or infrastructure changes.', margin + 5, y + 8);
    return y + 20;
  }

  issues.slice(0, 5).forEach((issue, index) => {
    const recommendation = issue.recommendation || issue.message || 'Review this finding and apply the recommended fix.';
    const lines = pdf.splitTextToSize(recommendation, contentWidth - 58) as string[];
    const rowHeight = Math.max(18, lines.length * 4 + 10);
    y = ensurePdfSpace(pdf, y, rowHeight + 4);
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(218, 228, 240);
    pdf.roundedRect(margin, y, contentWidth, rowHeight, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(scoreColor(issue.score ?? 0));
    pdf.text(`Priority ${index + 1}`, margin + 5, y + 7);
    pdf.setTextColor(15, 54, 99);
    pdf.text(formatCheckName(issue.checkName), margin + 27, y + 7);
    pdf.setTextColor(82, 102, 132);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Impact: ${issue.severity} | Effort: Review`, margin + 5, y + 14);
    pdf.text(lines, margin + 57, y + 14);
    y += rowHeight + 4;
  });

  return y + 4;
}

function generateNativeReportPdf(
  result: BrandingAnalyzeResponse,
  scanType: ScanType,
  categories: string[],
  grouped: Record<string, CheckResult[]>,
  logoDataUrl: string | null,
) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const checks = result.checks ?? [];
  const passed = checks.filter((check) => check.status === 'PASS').length;
  const warnings = checks.filter((check) => check.status === 'WARNING').length;
  const failed = checks.filter((check) => check.status === 'FAIL').length;
  const issues = checks
    .filter((check) => check.status === 'FAIL' || check.status === 'WARNING')
    .sort((a, b) => {
      const severityDifference =
        (SEVERITY_WEIGHT[b.severity] ?? 0) - (SEVERITY_WEIGHT[a.severity] ?? 0);
      if (severityDifference !== 0) return severityDifference;
      return (a.score ?? 0) - (b.score ?? 0);
    })
    .slice(0, 3);
  const potentialScore = estimatedPotentialScore(result);
  let y = 34;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Website Health Check Report', margin, y);
  y += 8;

  pdf.setFillColor(248, 251, 254);
  pdf.setDrawColor(218, 228, 240);
  pdf.roundedRect(margin, y, contentWidth, 30, 3, 3, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Report prepared for', margin + 6, y + 8);
  pdf.setFontSize(13);
  pdf.text(recipientLabel(result), margin + 6, y + 16);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(82, 102, 132);
  const recipientDetails = [
    result.customerEmail ? `Email: ${result.customerEmail}` : null,
    result.domain ? `Website: ${result.domain}` : null,
  ].filter(Boolean).join('   |   ');
  pdf.text(recipientDetails || 'Website report recipient', margin + 6, y + 24);
  y += 40;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(82, 102, 132);
  y = writeWrappedText(pdf, result.summary ?? '', margin, y, contentWidth, 5) + 4;

  pdf.setFillColor(248, 251, 254);
  pdf.setDrawColor(218, 228, 240);
  pdf.roundedRect(margin, y, contentWidth, 28, 3, 3, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(21, 128, 61);
  pdf.text(`${result.overallScore}/100`, margin + 6, y + 11);
  pdf.setFontSize(11);
  pdf.setTextColor(15, 54, 99);
  pdf.text(`Grade ${result.overallGrade}`, margin + 6, y + 20);
  pdf.setFontSize(10);
  pdf.text(`${passed} passed`, margin + 56, y + 11);
  pdf.text(`${warnings} warnings`, margin + 96, y + 11);
  pdf.text(`${failed} issues`, margin + 142, y + 11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(82, 102, 132);
  pdf.text(result.domain ?? '', margin + 56, y + 20);
  y += 38;

  y = drawPdfActionPlan(pdf, issues, margin, y, contentWidth);

  if (categories.length > 1) {
    y = ensurePdfSpace(pdf, y, 50);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(15, 54, 99);
    pdf.text(scanType === 'SITE' ? 'Health Score Overview' : 'Category Overview', margin, y);
    y += 8;

    const cardsPerRow = 4;
    const cardGap = 5;
    const cardWidth = (contentWidth - cardGap * (cardsPerRow - 1)) / cardsPerRow;
    const cardHeight = 34;

    categories.forEach((category, index) => {
      const column = index % cardsPerRow;
      const cardX = margin + column * (cardWidth + cardGap);
      if (column === 0) {
        y = ensurePdfSpace(pdf, y, cardHeight + 8);
      }

      const data = categoryMetrics(grouped[category]);
      const [r, g, b] = scorePdfColor(data.score);

      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(218, 228, 240);
      pdf.roundedRect(cardX, y, cardWidth, cardHeight, 3, 3, 'FD');
      pdf.setDrawColor(r, g, b);
      pdf.setLineWidth(0.5);
      pdf.line(cardX + 2, y + cardHeight - 1.5, cardX + cardWidth - 2, y + cardHeight - 1.5);
      pdf.setLineWidth(0.2);

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.setTextColor(15, 54, 99);
      const categoryLines = pdf.splitTextToSize(CATEGORY_LABELS[category] ?? category, cardWidth - 8) as string[];
      pdf.text(categoryLines.slice(0, 2), cardX + cardWidth / 2, y + 6, { align: 'center' });

      pdf.setDrawColor(r, g, b);
      pdf.setFillColor(255, 255, 255);
      pdf.circle(cardX + cardWidth / 2, y + 17, 6, 'S');
      pdf.setFontSize(10);
      pdf.setTextColor(r, g, b);
      pdf.text(String(data.score), cardX + cardWidth / 2, y + 18.5, { align: 'center' });

      pdf.setFontSize(7);
      pdf.setTextColor(r, g, b);
      pdf.text(scoreLabel(data.score), cardX + cardWidth / 2, y + 26, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(82, 102, 132);
      const detailText = `${data.passed} passed · ${data.warnings} warning${data.warnings === 1 ? '' : 's'} · ${data.failed} issue${data.failed === 1 ? '' : 's'}`;
      pdf.text(detailText, cardX + cardWidth / 2, y + 31, { align: 'center' });

      if (column === cardsPerRow - 1 || index === categories.length - 1) {
        y += cardHeight + 6;
      }
    });
    y += 2;
  }

  y = ensurePdfSpace(pdf, y, 56);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Executive Summary', margin, y);
  y += 8;

  const summaryCardGap = 6;
  const summaryCardWidth = (contentWidth - summaryCardGap) / 2;
  const summaryCardY = y;
  const topIssueRows = issues.map((issue) => ({
    issue,
    messageLines: (pdf.splitTextToSize(issue.message ?? '', summaryCardWidth - 15) as string[]).slice(0, 2),
  }));
  const topIssuesContentHeight = topIssueRows.length
    ? topIssueRows.reduce((height, row) => height + 7 + row.messageLines.length * 4 + 2, 0)
    : 9;
  const summaryCardHeight = Math.max(62, 23 + topIssuesContentHeight);

  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(218, 228, 240);
  pdf.roundedRect(margin, summaryCardY, summaryCardWidth, summaryCardHeight, 3, 3, 'FD');
  pdf.roundedRect(margin + summaryCardWidth + summaryCardGap, summaryCardY, summaryCardWidth, summaryCardHeight, 3, 3, 'FD');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Top Issues to Improve', margin + 5, summaryCardY + 8);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(82, 102, 132);
  pdf.text('Prioritised by severity and current check score.', margin + 5, summaryCardY + 14);

  let issueY = summaryCardY + 23;
  if (issues.length === 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(21, 128, 61);
    pdf.text('All checks passed. No immediate issue requires attention.', margin + 5, issueY);
  } else {
    topIssueRows.forEach(({ issue, messageLines }, index) => {
      const [r, g, b] = statusPdfColor(issue.status);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(r, g, b);
      pdf.text(`${index + 1}.`, margin + 5, issueY);
      pdf.setTextColor(15, 54, 99);
      pdf.text(formatCheckName(issue.checkName), margin + 10, issueY);
      pdf.setTextColor(r, g, b);
      pdf.text(issue.severity, margin + summaryCardWidth - 5, issueY, { align: 'right' });
      issueY += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(82, 102, 132);
      pdf.text(messageLines, margin + 10, issueY);
      issueY += messageLines.length * 4 + 2;
    });
  }

  const progressX = margin + summaryCardWidth + summaryCardGap + 5;
  const progressY = summaryCardY + 8;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Score Progress', progressX, progressY);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(82, 102, 132);
  pdf.text('Estimated potential after resolving detected issues.', progressX, progressY + 6);

  const barX = progressX;
  const barY = progressY + 16;
  const barWidth = summaryCardWidth - 10;
  pdf.setFillColor(230, 237, 245);
  pdf.roundedRect(barX, barY, barWidth, 3, 1.5, 1.5, 'F');
  pdf.setFillColor(22, 148, 71);
  pdf.roundedRect(barX, barY, Math.max(0, Math.min(100, result.overallScore)) / 100 * barWidth, 3, 1.5, 1.5, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(21, 128, 61);
  pdf.text(`${result.overallScore}`, progressX, progressY + 29);
  pdf.text(`${potentialScore}`, progressX + barWidth, progressY + 29, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(82, 102, 132);
  pdf.text('/100 current score', progressX + 8, progressY + 29);
  pdf.text('/100 estimated potential', progressX + barWidth - 8, progressY + 29, { align: 'right' });

  pdf.setFillColor(240, 253, 244);
  pdf.roundedRect(progressX, progressY + 35, barWidth, 11, 2, 2, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(22, 101, 52);
  const opportunityText = issues.length
    ? `Opportunity: Resolve the ${issues.length} highest-priority finding${issues.length === 1 ? '' : 's'} shown here, then scan again.`
    : 'Opportunity: Continue monitoring after content or infrastructure changes.';
  pdf.text(pdf.splitTextToSize(opportunityText, barWidth - 6) as string[], progressX + 3, progressY + 40);

  y = summaryCardY + summaryCardHeight + 12;

  y = ensurePdfSpace(pdf, y, 16);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Detailed Results', margin, y);
  y += 10;

  categories.forEach((category, index) => {
    const items = grouped[category] ?? [];
    const data = categoryMetrics(items);
    if (index > 0) y += 8;
    y = ensurePdfSpace(pdf, y, 36);
    pdf.setFillColor(248, 251, 254);
    pdf.setDrawColor(218, 228, 240);
    pdf.roundedRect(margin, y, contentWidth, 14, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(15, 54, 99);
    pdf.text(CATEGORY_LABELS[category] ?? category, margin + 4, y + 7);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(82, 102, 132);
    pdf.text(`${data.passed} passed | ${data.warnings} warnings | ${data.failed} issues | ${data.score}/100`, pageWidth - margin - 4, y + 8, { align: 'right' });
    y += 20;
    const context = CATEGORY_CONTEXT[category];
    if (context) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(82, 102, 132);
      y = writeWrappedText(pdf, context, margin + 4, y - 2, contentWidth - 8, 4) + 4;
    }

    const actionableItems = items.filter((check) => check.status === 'FAIL' || check.status === 'WARNING');
    const passedItems = items.filter((check) => check.status === 'PASS');
    y = drawPdfPassedSummary(pdf, passedItems, margin, y, contentWidth);

    if (actionableItems.length) {
      y = ensurePdfSpace(pdf, y, 22);
      y = drawPdfTableHeader(pdf, margin, y, contentWidth);
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(21, 128, 61);
      pdf.text('No findings need attention in this category.', margin + 4, y + 2);
      y += 8;
    }

    actionableItems.forEach((check) => {
      const recommendation =
        (check.status === 'FAIL' || check.status === 'WARNING') && check.recommendation
          ? check.recommendation
          : '';
      const checkLines = (pdf.splitTextToSize(formatCheckName(check.checkName), 27) as string[]).slice(0, 3);
      const detailLines = pdf.splitTextToSize(check.message ?? '', 39) as string[];
      const recommendationLines = pdf.splitTextToSize(recommendation, contentWidth - 141) as string[];
      const rowHeight = Math.max(13, checkLines.length * 4 + 6, detailLines.length * 4 + 6, recommendationLines.length * 4 + 6);
      if (y + rowHeight > pdf.internal.pageSize.getHeight() - 26) {
        pdf.addPage();
        y = 32;
        y = drawPdfTableHeader(pdf, margin, y, contentWidth);
      }
      y = drawPdfDetailTableRow(pdf, check, margin, y, contentWidth);
    });

    y += 6;
  });

  const totalPages = pdf.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    pdf.setPage(pageNumber);
    drawPdfBranding(pdf, pageNumber, totalPages, result, logoDataUrl);
  }

  return pdf;
}

export default function WebsiteHealthReport({
  result,
  scanType,
  showEmail = false,
  onScanAgain,
}: Props) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const checks = result.checks ?? [];
  const grouped = groupChecks(checks);
  const preferredOrder = CATEGORY_ORDER_BY_SCAN[scanType] ?? [];
  const categories = [
    ...preferredOrder.filter((category) => grouped[category]?.length),
    ...Object.keys(grouped).filter((category) => !preferredOrder.includes(category)),
  ];

  const issues = checks
    .filter((check) => check.status === 'FAIL' || check.status === 'WARNING')
    .sort((a, b) => {
      const severityDifference =
        (SEVERITY_WEIGHT[b.severity] ?? 0) - (SEVERITY_WEIGHT[a.severity] ?? 0);
      if (severityDifference !== 0) return severityDifference;
      return (a.score ?? 0) - (b.score ?? 0);
    })
    .slice(0, 3);

  const potentialScore = estimatedPotentialScore(result);

  const handleDownloadPdf = async () => {
    if (downloadingPdf) return;

    setDownloadingPdf(true);

    try {
      const logoDataUrl = await loadImageDataUrl(ASNERIS_LOGO_PATH);
      const pdf = generateNativeReportPdf(result, scanType, categories, grouped, logoDataUrl);
      pdf.save(`${safeFilenamePart(result.domain ?? result.scanId)}-health-report.pdf`);
    } catch (error) {
      console.error('PDF download failed:', error);
      window.alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="asneris-report" style={{ animation: 'asnerisReportIn .35s ease' }}>
      <style>{`
        @keyframes asnerisReportIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .asneris-report-card {
          background: #fff;
          border: 1px solid rgba(15, 54, 99, .12);
          border-radius: 14px;
          box-shadow: 0 10px 28px rgba(4, 34, 73, .08);
        }
        .asneris-category-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
          gap: 12px;
        }
        .asneris-report-two-column {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(280px, .85fr);
          gap: 16px;
        }
        @media (max-width: 820px) {
          .asneris-report-two-column { grid-template-columns: 1fr; }
        }
      `}</style>

      <div style={{ background: '#f8fbfe', padding: '1px' }}>
        <section className="asneris-report-card" style={{ padding: '22px' }}>
          <ScanResultSummary result={result} />
          {showEmail && (
            <div data-html2canvas-ignore="true">
              <EmailReportCapture
                scanId={result.scanId}
                domain={result.domain ?? ''}
                defaultEmail={result.customerEmail ?? ''}
              />
            </div>
          )}
        </section>

        {categories.length > 1 && (
          <section className="asneris-report-card" style={{ marginTop: '16px', padding: '20px' }}>
            <h2 style={{ margin: '0 0 14px', fontSize: '17px', color: 'var(--asneris-navy)' }}>
              {scanType === 'SITE' ? 'Health Score Overview' : 'Category Overview'}
            </h2>
            <div className="asneris-category-grid">
              {categories.map((category) => {
                const metrics = categoryMetrics(grouped[category]);
                const color = scoreColor(metrics.score);
                return (
                  <div
                    key={category}
                    style={{
                      border: '1px solid var(--asneris-border)',
                      borderRadius: '10px',
                      padding: '14px 12px',
                      textAlign: 'center',
                      borderBottom: `3px solid ${color}`,
                      background: '#fff',
                    }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--asneris-navy)' }}>
                      {CATEGORY_LABELS[category] ?? category}
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '25px', fontWeight: 800, color }}>
                      {metrics.score}
                    </div>
                    <div style={{ marginTop: '2px', fontSize: '11px', fontWeight: 600, color }}>
                      {scoreLabel(metrics.score)}
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--asneris-muted)' }}>
                      {metrics.passed} passed
                      {metrics.warnings > 0 ? ` · ${metrics.warnings} warning${metrics.warnings === 1 ? '' : 's'}` : ''}
                      {metrics.failed > 0 ? ` · ${metrics.failed} issue${metrics.failed === 1 ? '' : 's'}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="asneris-report-two-column" style={{ marginTop: '16px' }}>
          <section className="asneris-report-card" style={{ padding: '20px' }}>
            <h2 style={{ margin: '0 0 5px', fontSize: '17px', color: 'var(--asneris-navy)' }}>
              Top Issues to Improve
            </h2>
            <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--asneris-muted)' }}>
              Prioritised by severity and current check score.
            </p>

            {issues.length === 0 ? (
              <div
                style={{
                  padding: '18px',
                  borderRadius: '10px',
                  background: '#ecfdf5',
                  color: '#166534',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                All checks passed. No immediate issue requires attention.
              </div>
            ) : (
              <div>
                {issues.map((issue, index) => (
                  <div
                    key={`${issue.category}-${issue.checkName}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '30px minmax(0, 1fr) auto',
                      gap: '10px',
                      padding: '12px 0',
                      borderBottom:
                        index === issues.length - 1 ? 'none' : '1px solid var(--asneris-border)',
                    }}
                  >
                    <span
                      style={{
                        width: 25,
                        height: 25,
                        borderRadius: '50%',
                        border: `1px solid ${issue.status === 'FAIL' ? '#dc2626' : '#f59e0b'}`,
                        color: issue.status === 'FAIL' ? '#dc2626' : '#b45309',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--asneris-navy)' }}>
                        {formatCheckName(issue.checkName)}
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: '12px', lineHeight: 1.5, color: 'var(--asneris-muted)' }}>
                        {issue.message}
                      </p>
                    </div>
                    <span
                      className={
                        issue.severity === 'HIGH'
                          ? 'asneris-badge asneris-badge-error'
                          : issue.severity === 'MEDIUM'
                            ? 'asneris-badge asneris-badge-warning'
                            : 'asneris-badge asneris-badge-info'
                      }
                      style={{ alignSelf: 'start', whiteSpace: 'nowrap' }}
                    >
                      {issue.severity}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="asneris-report-card" style={{ padding: '20px' }}>
            <h2 style={{ margin: '0 0 5px', fontSize: '17px', color: 'var(--asneris-navy)' }}>
              Score Progress
            </h2>
            <p style={{ margin: '0 0 18px', fontSize: '12px', color: 'var(--asneris-muted)' }}>
              Estimated potential after resolving the detected issues.
            </p>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={result.overallScore}
              style={{
                height: 10,
                borderRadius: 999,
                background: '#e6edf5',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.max(0, Math.min(100, result.overallScore))}%`,
                  background: '#169447',
                  borderRadius: 999,
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
              <div>
                <strong style={{ color: '#15803d', fontSize: '18px' }}>{result.overallScore}</strong>
                <span style={{ fontSize: '12px', color: 'var(--asneris-muted)' }}>/100</span>
                <div style={{ fontSize: '11px', color: 'var(--asneris-muted)' }}>Current score</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ color: '#15803d', fontSize: '18px' }}>{potentialScore}</strong>
                <span style={{ fontSize: '12px', color: 'var(--asneris-muted)' }}>/100</span>
                <div style={{ fontSize: '11px', color: 'var(--asneris-muted)' }}>
                  Estimated potential
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: '18px',
                padding: '14px',
                borderRadius: '10px',
                background: '#f0fdf4',
                color: '#166534',
                fontSize: '12px',
                lineHeight: 1.55,
              }}
            >
              <strong>Opportunity:</strong>{' '}
              {issues.length
                ? `Resolve the ${issues.length} highest-priority finding${issues.length === 1 ? '' : 's'} shown here, then scan again.`
                : 'Continue monitoring the website after content or infrastructure changes.'}
            </div>
          </section>
        </div>

        <section className="asneris-report-card" style={{ marginTop: '16px', padding: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '17px', color: 'var(--asneris-navy)' }}>
              Detailed Results
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--asneris-muted)' }}>
              {checks.length} checks grouped by diagnostic category.
            </p>
          </div>
          <CheckCategoryAccordion checks={checks} />
        </section>
      </div>

      <PublicCtaBox
        onScanAgain={onScanAgain}
        onDownload={handleDownloadPdf}
        downloadLabel={downloadingPdf ? 'Preparing PDF...' : 'Download PDF'}
        downloadDisabled={downloadingPdf}
        learnMoreHref={LEARN_MORE_HREF_BY_SCAN[scanType]}
      />
    </div>
  );
}